/**
 * AcquisitionMix.tsx — Section 2
 * One card per sign-up method, each as % of new Members with a thin progress
 * bar. Provider-generic: the cards are whatever the API returns, sorted by
 * volume — the old hardcoded four-provider list could never render a fifth
 * card, which is how live Yahoo members stayed invisible here for months.
 *
 * Percentages use largest-remainder rounding so the displayed integers sum to
 * exactly 100 when the providers cover the whole cohort; a provider under
 * 0.5% shows one decimal instead of a false "0%".
 *
 * Data: live auth_method_breakdown + funnel_top.
 */

import React from 'react';
import { Box, Typography, Paper, Skeleton } from '@mui/material';
import SectionHeader from './SectionHeader';
import { FONT, FONT_MONO, METHOD_COLORS, SURFACE, formatShares } from './funnelBands';
import { AuthMethod } from './activationTypes';

interface Props {
  methods: AuthMethod[];
  funnelTop: number;
  loading: boolean;
}

interface RowShare {
  key?: string;
  method: string;
  value: number;
  pct: number;        // exact share of funnelTop
  displayPct: string; // largest-remainder-rounded, sums to 100 when exhaustive
}

const AcquisitionMix: React.FC<Props> = ({ methods, funnelTop, loading }) => {
  // formatShares is THE provider-share formatter (funnelBands) — the heat
  // strip's auth panel uses the same one, so one provider can no longer print
  // two different percentages on one page.
  const sorted = [...methods].sort((a, b) => b.value - a.value);
  const shares = formatShares(sorted.map(m => m.value), funnelTop);
  const rows: RowShare[] = sorted.map((m, i) => ({
    key: m.key,
    method: m.method,
    value: m.value,
    pct: funnelTop > 0 ? (m.value / funnelTop) * 100 : 0,
    displayPct: shares[i],
  }));

  // Lowest-share live provider — INCLUDING zero-value ones (the old code
  // filtered zeros out, so "lowest-share path" wasn't the lowest share).
  const lowest = rows.length > 1 ? rows.reduce((min, r) => (r.pct < min.pct ? r : min), rows[0]) : null;

  return (
    <Box>
      <SectionHeader
        num={2}
        title="Acquisition Mix"
        description={`Shows which sign-up method each of the ${funnelTop.toLocaleString()} new Members used to create their account, as a % of the cohort.`}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${Math.min(Math.max(rows.length, 2), 5)}, 1fr)` }, gap: 1.5 }}>
        {(loading ? Array(4).fill(null) : rows).map((r: RowShare | null, idx: number) => {
          const color = r ? (METHOD_COLORS[r.key ?? ''] || '#5D6D7E') : '#1A5F7A';
          return (
            <Box key={r?.key ?? r?.method ?? idx}>
              {loading || !r ? (
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '13px' }} />
              ) : (
                <Paper elevation={0} sx={{
                  bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`,
                  borderLeft: `4px solid ${color}`, borderRadius: '13px', p: '13px 15px', height: '100%',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: FONT, fontSize: r.method.length > 20 ? '10.5px' : '12px', fontWeight: 600, color: SURFACE.ink, lineHeight: 1.2 }}>
                      {r.method}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: FONT_MONO, fontSize: '26px', fontWeight: 800, lineHeight: 1, color, mb: 0.4 }}>
                    {r.displayPct}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: SURFACE.slate }}>
                    {r.value > 0
                      ? `${r.value.toLocaleString()} of ${funnelTop.toLocaleString()} new Members`
                      : 'no sign-ups this period'}
                  </Typography>
                  <Box sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: '#EFEAE0', overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.min(100, r.pct)}%`, bgcolor: color }} />
                  </Box>
                </Paper>
              )}
            </Box>
          );
        })}
      </Box>

      {!loading && lowest && (
        <Box sx={{ mt: 1.5, bgcolor: '#FFF8E8', border: '1px solid #EADFC2', borderRadius: '10px', p: '11px 15px' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#6A5B3E', lineHeight: 1.5 }}>
            <b style={{ color: '#B8860B' }}>★ Lowest-share provider:</b> {lowest.method} at {lowest.displayPct}
            {lowest.value === 0 ? ' (no sign-ups this period)' : ''} — if it is the lowest-friction option for this
            audience, it may be buried on the auth screen; worth an A/B on placement.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AcquisitionMix;
